import {
  Address,
  Center,
  CentersManyResponse,
  Contact,
  CreateCenterDto,
  UpdateCenterDto,
} from 'src/api/booking/data-contracts';
import {
  QueriedData,
  showErrorNotification,
  showSuccessNotification,
} from '../assets/useAssets';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Centers } from 'src/api/booking/Centers';
import { defaultBookingApiConfig } from 'src/api';

export interface UseCentersResult {
  centersQuery: QueriedData<CentersManyResponse>;
  createCenter: (center: CreateCenterDto) => void;
  archiveCenter: (centerID: string) => void;
}

function useCreateCenter() {
  const queryClient = useQueryClient();
  const { mutate: createCenter } = useMutation(
    (center: CreateCenterDto) =>
      centersApi.createCenter(center).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('Centro criado com sucesso');
        queryClient.invalidateQueries(['centers']);
      },
      onError: () => {
        showErrorNotification('Ocorreu um erro inesperado');
      },
    },
  );
  return createCenter;
}

const centersApi = new Centers(defaultBookingApiConfig);
export function useCenters(): UseCentersResult {
  const query = useQuery({
    queryFn: async () => centersApi.findCenters().then((r) => r.data),
    queryKey: ['centers'],
  });

  const createCenter = useCreateCenter();
  const queryClient = useQueryClient();
  const { mutate: archiveCenter } = useMutation(
    (centerID: string) =>
      centersApi.archiveCenter(centerID).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('Centro arquivado com sucesso');
        queryClient.invalidateQueries(['centers']);
      },
      onError: () => showErrorNotification('Ocorreu um erro inseperado'),
    },
  );
  return { centersQuery: query, createCenter, archiveCenter };
}

export interface useCenterByIDResult {
  centerQuery: QueriedData<Center>;
  updateCenter: (center: { name: string }) => void;
  addAddress: (address: Address) => void;
  updateAddress: (data: { indx: number; address: Address }) => void;
  deleteAddress: (indx: number) => void;
  updateContact: (data: { indx: number; contact: Contact }) => void;
  addContact: (contact: Contact) => void;
  deleteContact: (indx: number) => void;
}

export function useUpdateCenter() {
  const queryClient = useQueryClient();

  const { mutate: updateCenter } = useMutation(
    ({ id, center }: { id: string; center: UpdateCenterDto }) =>
      centersApi.updateCenter(id, center).then((res) => res.data),
    {
      onSuccess: (_, { id }) => {
        showSuccessNotification('Centro criado com sucesso');
        queryClient.invalidateQueries(['centers', id]);
        queryClient.invalidateQueries(['centers']);
      },
      onError: () => {
        showErrorNotification('Ocorreu um erro inesperado');
      },
    },
  );
  return updateCenter;
}
export function useCenterByID(id: string): useCenterByIDResult {
  const queryKey = ['centers', id];
  const query = useQuery({
    queryFn: async () => centersApi.findCenterById(id).then((r) => r.data),
    queryKey,
  });

  const queryClient = useQueryClient();
  const updateCenter = useUpdateCenter();

  const update = (data: { name: string }) => {
    const center = getCenter();
    center.name = data.name;
    updateCenter({ id, center });
  };
  function invalidateQueries() {
    queryClient.invalidateQueries(['centers', id]);
    queryClient.invalidateQueries(['centers']);
  }

  function getCenter(): Center {
    if (!query.data || query.isLoading || query.isError) {
      throw new Error('Ocorreu um erro inesperado');
    }
    return { ...query.data };
  }
  const { mutate: addAddress } = useMutation(
    (address: Address) => {
      const center = getCenter();
      center.addresses.push(address);
      return centersApi.updateCenter(id, center);
    },
    {
      onSuccess: () => {
        showSuccessNotification('Morada criada com sucesso');
        invalidateQueries();
      },
      onError: () => {
        showErrorNotification('Não foi possível criar a morada');
      },
    },
  );
  const { mutate: updateAddress } = useMutation(
    (data: { indx: number; address: Address }) => {
      const center = getCenter();
      center.addresses[data.indx] = data.address;
      return centersApi.updateCenter(id, center);
    },
    {
      onSuccess: () => {
        showSuccessNotification('Morada atualizada com sucesso');
        invalidateQueries();
      },
      onError: () => {
        showErrorNotification('Não foi possível atualizar a morada');
      },
    },
  );
  const { mutate: deleteAddress } = useMutation(
    (index: number) => {
      const center = getCenter();
      center.addresses = center.addresses.filter((_, i) => index !== i);
      return centersApi.updateCenter(id, center);
    },
    {
      onSuccess: () => {
        showSuccessNotification('Moarada eliminada com sucesso');
        invalidateQueries();
      },
      onError: () => {
        showErrorNotification('Não foi possível eliminar a morada');
      },
    },
  );
  const { mutate: addContact } = useMutation(
    (contact: Contact) => {
      const center = getCenter();
      center.contacts.push(contact);
      return centersApi.updateCenter(id, center);
    },
    {
      onSuccess: () => {
        showSuccessNotification('Contacto criado com sucesso');
        invalidateQueries();
      },
      onError: () => {
        showErrorNotification('Não foi possível criar o contacto');
      },
    },
  );
  const { mutate: updateContact } = useMutation(
    (data: { indx: number; contact: Contact }) => {
      const center = getCenter();
      center.contacts[data.indx] = data.contact;
      return centersApi.updateCenter(id, center);
    },
    {
      onSuccess: () => {
        showSuccessNotification('Contacto atualizado com sucesso');
        invalidateQueries();
      },
      onError: () => {
        showErrorNotification('Não foi possível atualizar o contacto');
      },
    },
  );
  const { mutate: deleteContact } = useMutation(
    (index: number) => {
      const center = getCenter();
      center.contacts = center.contacts.filter((_, i) => index !== i);
      return centersApi.updateCenter(id, center);
    },
    {
      onSuccess: () => {
        showSuccessNotification('Contacto eliminado com sucesso');
        invalidateQueries();
      },
      onError: () => {
        showErrorNotification('Não foi possível eliminar o contacto');
      },
    },
  );

  return {
    centerQuery: query,
    updateCenter: update,
    addAddress,
    updateAddress,
    deleteAddress,
    addContact,
    updateContact,
    deleteContact,
  };
}
